import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, ChevronDown, ChevronRight, Circle, Settings, Zap, Users, Plus, Edit, Trash2, Sparkles, CheckCircle, Eye } from 'lucide-react';
import { ScoringAgentPanel } from '@/components/automation/qa/ScoringAgentPanel';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { selectAllTeams, selectTeamsLoading } from '@/store/slices/teams/selectors';
import { fetchTeams } from '@/store/slices/teams/teamsSlice';
const AutoQA = () => {
  const {
    setHeaderData
  } = usePageHeader();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teams = useAppSelector(selectAllTeams);
  const teamsLoading = useAppSelector(selectTeamsLoading);
  const [isAutoQAEnabled, setIsAutoQAEnabled] = useState(false);
  const [isGeneralOpen, setIsGeneralOpen] = useState(true);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isTeamScoringAgentsOpen, setIsTeamScoringAgentsOpen] = useState(false);
  const [confidenceThreshold, setConfidenceThreshold] = useState([75]);
  const [qaFrequency, setQaFrequency] = useState('all');
  const [autoCorrection, setAutoCorrection] = useState(false);
  const [realTimeAnalysis, setRealTimeAnalysis] = useState(true);
  const [showScoringAgentDialog, setShowScoringAgentDialog] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);

  // Mock team scoring agents data - updated to match ScoringAgentPanel fields
  const [teamScoringAgents, setTeamScoringAgents] = useState([{
    id: '1',
    name: 'Customer Support Scorecard',
    description: 'Quality evaluation for customer support interactions',
    teamName: 'Support Team',
    status: 'live' as 'draft' | 'live',
    ratingScale: '0-100' as '1-5' | '1-10' | '0-100',
    qaDelay: '30 minutes',
    confidenceThreshold: 85,
    aiConfidenceThreshold: 80,
    frequency: 'all',
    agentParametersCount: 6,
    botParametersCount: 4,
    scoreBandsCount: 4,
    enabled: true,
    createdAt: '2024-01-15',
    lastModified: '2024-01-20'
  }, {
    id: '2', 
    name: 'Technical Support Scorecard',
    description: 'Specialized evaluation for technical support tickets',
    teamName: 'Technical Support',
    status: 'draft' as 'draft' | 'live',
    ratingScale: '1-10' as '1-5' | '1-10' | '0-100',
    qaDelay: '1 hour',
    confidenceThreshold: 90,
    aiConfidenceThreshold: 85,
    frequency: 'percentage',
    agentParametersCount: 8,
    botParametersCount: 5,
    scoreBandsCount: 4,
    enabled: false,
    createdAt: '2024-01-18',
    lastModified: '2024-01-19'
  }]);
  useEffect(() => {
    setHeaderData({
      title: 'Auto QA',
      description: 'Configure automated quality assurance settings',
      icon: Bot
    });
    return () => setHeaderData(null);
  }, [setHeaderData]);

  // Fetch teams on component mount
  useEffect(() => {
    if (teams.length === 0) {
      dispatch(fetchTeams());
    }
  }, [dispatch, teams.length]);

  // Transform teams data for the component
  const teamOptions = teams.map(team => ({
    id: team.id,
    name: team.name
  }));
  return <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <p className="text-gray-600 mb-6">
          Configure automated quality assurance for support responses and ticket handling.
        </p>
        
        {/* General Settings Section */}
        <Card className="border-border/60 bg-gradient-to-br from-card via-card/95 to-muted/20 shadow-sm">
          <Collapsible open={isGeneralOpen} onOpenChange={setIsGeneralOpen}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/20">
                    <Settings className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-foreground">
                      General Settings
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Basic Auto QA configuration
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!isGeneralOpen && <Badge variant={isAutoQAEnabled ? "default" : "secondary"} className="text-xs px-2 py-0.5 h-5 bg-muted/40 border-border/40">
                      {isAutoQAEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>}
                  
                  <Collapsible open={isGeneralOpen} onOpenChange={setIsGeneralOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-muted/50 transition-all duration-200">
                        {isGeneralOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" /> : <ChevronRight className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />}
                      </Button>
                    </CollapsibleTrigger>
                  </Collapsible>
                </div>
              </div>
            </CardHeader>

            <CollapsibleContent>
              <CardContent className="pt-0 space-y-6">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-muted/30 to-muted/10 rounded-lg border border-border/30">
                  <div className="flex items-center gap-3">
                    <Circle className={`h-3 w-3 ${isAutoQAEnabled ? 'text-emerald-500 fill-emerald-500' : 'text-gray-400 fill-gray-400'}`} />
                    <span className="text-sm font-medium text-foreground">
                      Auto QA Status: {isAutoQAEnabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <Badge variant={isAutoQAEnabled ? "default" : "secondary"}>
                    {isAutoQAEnabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="autoqa-enabled" className="text-base font-medium">
                        Enable Auto QA
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically analyze support responses for quality and compliance
                      </p>
                    </div>
                    <Switch id="autoqa-enabled" checked={isAutoQAEnabled} onCheckedChange={setIsAutoQAEnabled} />
                  </div>

                  {isAutoQAEnabled && <>
                      

                      
                    </>}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Team Level QA Rubrics Section */}
        {isAutoQAEnabled && <Card className="border-border/60 bg-gradient-to-br from-card via-card/95 to-muted/20 shadow-sm">
            <Collapsible open={isTeamScoringAgentsOpen} onOpenChange={setIsTeamScoringAgentsOpen}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 flex items-center justify-center border border-blue-500/20">
                      <Users className="h-4 w-4 text-blue-500" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        Team Scoring Agents
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Configure team-specific scoring agent parameters
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!isTeamScoringAgentsOpen && <div className="flex items-center gap-2 mr-2">
                        <div className="flex items-center gap-1">
                          {teamScoringAgents.slice(0, 3).map(agent => <div key={agent.id} className={`w-2 h-2 rounded-full shadow-sm ring-1 ring-white/20 ${agent.enabled ? 'bg-emerald-500' : 'bg-gray-400'}`} title={agent.teamName} />)}
                          {teamScoringAgents.length > 3 && <span className="text-xs text-muted-foreground ml-1">
                              +{teamScoringAgents.length - 3}
                            </span>}
                        </div>
                        <Badge variant="secondary" className="text-xs px-2 py-0.5 h-5 bg-muted/40 border-border/40">
                          {teamScoringAgents.filter(r => r.enabled).length}/{teamScoringAgents.length}
                        </Badge>
                      </div>}
                    
                     {isTeamScoringAgentsOpen && <Button onClick={() => {
                  setEditingTeamId(null);
                  setShowScoringAgentDialog(true);
                }} size="sm" className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 border-blue-500/20 hover:border-blue-500/30 transition-all duration-300 shadow-sm hover:shadow-md" variant="outline">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Team Scoring Agent
                      </Button>}
                    
                    <Collapsible open={isTeamScoringAgentsOpen} onOpenChange={setIsTeamScoringAgentsOpen}>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-muted/50 transition-all duration-200">
                          {isTeamScoringAgentsOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" /> : <ChevronRight className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />}
                        </Button>
                      </CollapsibleTrigger>
                    </Collapsible>
                  </div>
                </div>
              </CardHeader>

              <CollapsibleContent>
                <CardContent className="pt-0 space-y-6">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-muted/30 to-muted/10 rounded-lg border border-border/30">
                    <div className="flex items-center gap-3">
                      <Circle className="h-3 w-3 text-blue-500 fill-blue-500" />
                      <span className="text-sm font-medium text-foreground">
                        {teamScoringAgents.filter(r => r.enabled).length} Active Team Scoring Agents
                      </span>
                      <div className="w-px h-4 bg-border" />
                      <span className="text-sm text-muted-foreground">
                        {teamScoringAgents.length} Total Teams
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {teamScoringAgents.slice(0, 5).map(agent => <div key={agent.id} className={`w-2.5 h-2.5 rounded-full shadow-sm ring-1 ring-white/20 ${agent.enabled ? 'bg-emerald-500' : 'bg-gray-400'}`} title={agent.teamName} />)}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {teamScoringAgents.map(agent => (
                      <div key={agent.id} className="group flex items-center gap-3 p-4 bg-gradient-to-r from-background to-muted/20 border border-border/50 rounded-lg transition-all duration-200 ease-in-out hover:shadow-sm hover:border-border/80 hover:bg-muted/30">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="inline-flex items-center gap-2 px-3 py-1 font-medium">
                                <Users className="h-3 w-3" />
                                {agent.teamName}
                              </Badge>
                              <Badge variant={agent.status === 'live' ? "default" : "secondary"} className="text-xs">
                                {agent.status === 'live' ? 'Live' : 'Draft'}
                              </Badge>
                              <Badge variant={agent.enabled ? "default" : "secondary"} className="text-xs">
                                {agent.enabled ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="mb-2">
                            <h4 className="font-medium text-sm">{agent.name}</h4>
                            <p className="text-xs text-muted-foreground">{agent.description}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Scale:</span>
                              <div className="font-medium">{agent.ratingScale}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">QA Delay:</span>
                              <div className="font-medium">{agent.qaDelay}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Agent Params:</span>
                              <div className="font-medium">{agent.agentParametersCount}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Bot Params:</span>
                              <div className="font-medium">{agent.botParametersCount}</div>
                            </div>
                          </div>
                          
                          <div className="mt-2 grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Confidence:</span>
                              <div className="font-medium">{agent.confidenceThreshold}%</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">AI Confidence:</span>
                              <div className="font-medium">{agent.aiConfidenceThreshold}%</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Score Bands:</span>
                              <div className="font-medium">{agent.scoreBandsCount}</div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" onClick={() => {
                      navigate(`/home/settings/autoqa/scoring-agents/${agent.id}`);
                    }} className="h-8 w-8 p-0">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Team Scoring Agent</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete the "{agent.teamName}" scoring agent? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => {
                            setTeamScoringAgents(prev => prev.filter(r => r.id !== agent.id));
                          }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                    
                    {teamScoringAgents.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">No team scoring agents configured</p>
                        <p className="text-sm">Add team-specific scoring agent parameters to get started</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}

        {/* Advanced Settings Section */}
        {isAutoQAEnabled}

        {/* Modern Scoring Agent Configuration Dialog */}
        <Dialog open={showScoringAgentDialog} onOpenChange={setShowScoringAgentDialog}>
          <DialogContent className="max-w-6xl w-[90vw] h-[90vh] max-h-[90vh] p-0 bg-background/95 backdrop-blur-xl border border-border/20 shadow-xl rounded-2xl overflow-hidden flex flex-col">
            {/* Elegant Header */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b border-border/10 bg-gradient-to-r from-muted/30 via-background to-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 flex items-center justify-center border border-primary/15 shadow-sm ring-1 ring-primary/5">
                    <Settings className="h-5 w-5 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <DialogTitle className="text-xl font-semibold text-foreground">
                      {editingTeamId ? 'Edit Scoring Agent' : 'Create Scoring Agent'}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                      {editingTeamId ? 'Modify scoring parameters for this team' : 'Configure AI-powered quality evaluation parameters'}
                    </p>
                  </div>
                </div>
                
                {/* Modern Status Badge */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/30">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Live Setup</span>
                  </div>
                </div>
              </div>
            </DialogHeader>
            
            {/* Content Area with Better Visual Structure */}
            <div className="flex-1 min-h-0 bg-gradient-to-b from-background/80 to-muted/10">
              <div className="h-full overflow-y-auto overflow-x-hidden custom-scrollbar">
                <div className="p-6 space-y-6">
                  {/* Welcome Card for New Agents */}
                  {!editingTeamId}
                  
                  {/* Enhanced Configuration Panel */}
                  <div className="bg-card/60 backdrop-blur-sm rounded-2xl border border-border/20 shadow-sm ring-1 ring-border/5 overflow-hidden">
                    <ScoringAgentPanel teamId={editingTeamId || 'new'} teams={teamOptions} onSave={weights => {
                    console.log('Saving scoring agent weights:', weights);
                    // Here you would normally save to your backend
                    setShowScoringAgentDialog(false);
                    setEditingTeamId(null);
                  }} onCancel={() => {
                    setShowScoringAgentDialog(false);
                    setEditingTeamId(null);
                  }} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modern Footer with Enhanced Actions */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-border/10 bg-gradient-to-r from-muted/20 via-background to-muted/20 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="font-medium">Configuration saved automatically</span>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => {
                  setShowScoringAgentDialog(false);
                  setEditingTeamId(null);
                }} className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:bg-muted/50 rounded-lg">
                    Cancel
                  </Button>
                  <Button size="sm" className="bg-gradient-to-r from-primary via-primary to-primary/90 hover:from-primary/90 hover:via-primary/95 hover:to-primary/80 shadow-md hover:shadow-lg transition-all duration-300 rounded-lg font-medium" onClick={() => {
                  setShowScoringAgentDialog(false);
                  setEditingTeamId(null);
                }}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {editingTeamId ? 'Update Agent' : 'Create Agent'}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>;
};
export default AutoQA;